import React from "react";
import { ArrowRight, ChevronDown } from "lucide-react";
import { Link } from "@/lib/router-compat";
import SaintVideoSection from "./SaintVideoSection";
import { RelatedLink } from "./RelatedPages";
import { FAQItem } from "./DepressionContentPage";

/* ── FAQ accordion item (native details/summary for a11y) ── */
export const FAQAccordionItem: React.FC<{
  item: FAQItem;
  idx: number;
}> = ({ item, idx }) => (
  <details
    className="group rounded-xl border border-border bg-card shadow-soft overflow-hidden"
    name={`faq-${idx}`}
  >
    <summary className="flex cursor-pointer items-center justify-between gap-4 p-5 font-headings text-base sm:text-lg font-bold text-primary list-none [&::-webkit-details-marker]:hidden">
      <span>{item.question}</span>
      <ChevronDown className="h-5 w-5 shrink-0 text-accent transition-transform group-open:rotate-180" />
    </summary>
    <div className="px-5 pb-5 text-foreground/80 text-base leading-relaxed">
      {item.answer}
    </div>
  </details>
);

/* ── Comparison table → cards on mobile ── */
export const ComparisonBlock: React.FC<{
  headers: string[];
  rows: { feature: string; columns: string[] }[];
}> = ({ headers, rows }) => (
  <div className="overflow-hidden rounded-2xl border border-border shadow-soft">
    {/* Desktop table */}
    <div className="hidden md:block overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="bg-gradient-navy text-primary-foreground">
            <th className="px-5 py-4 font-headings font-bold text-base">
              {headers[0]}
            </th>
            {headers.slice(1).map((h, i) => (
              <th
                key={i}
                className="px-5 py-4 font-headings font-bold text-base text-center"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className={i % 2 === 0 ? "bg-card" : "bg-light-blue/30"}
            >
              <td className="px-5 py-4 font-semibold text-primary align-top">
                {row.feature}
              </td>
              {row.columns.map((col, j) => (
                <td
                  key={j}
                  className="px-5 py-4 text-foreground/80 text-center align-top leading-relaxed"
                >
                  {col}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {/* Mobile cards */}
    <div className="md:hidden divide-y divide-border">
      {rows.map((row, i) => (
        <div key={i} className="p-4 bg-card">
          <p className="font-headings font-bold text-primary text-base mb-3">
            {row.feature}
          </p>
          {headers.slice(1).map((h, j) => (
            <div key={j} className="mb-2 last:mb-0">
              <span className="block text-xs font-semibold text-accent uppercase tracking-wide mb-0.5">
                {h}
              </span>
              <span className="block text-foreground/80 text-sm leading-relaxed">
                {row.columns[j]}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  </div>
);

export interface ContentSection {
  id?: string;
  heading?: string;
  variant?:
    | "text"
    | "cards"
    | "split"
    | "comparison"
    | "callout"
    | "steps"
    | "links"
    | "video";
  videoVariant?: "full" | "compact" | "cost";
  intro?: string;
  body?: React.ReactNode;
  cards?: { title: string; body: string; icon?: string }[];
  steps?: { title: string; body: string }[];
  comparison?: {
    headers: string[];
    rows: { feature: string; columns: string[] }[];
  };
  callout?: string;
  links?: RelatedLink[];
}

/* ── Section renderer ── */
export const SectionRenderer: React.FC<{
  section: ContentSection;
  idx: number;
}> = ({ section, idx }) => {
  const variant = section.variant || "text";

  // Video variant renders full-width (its own background + container)
  if (variant === "video") {
    return <SaintVideoSection variant={section.videoVariant || "full"} />;
  }

  const isLight = idx % 2 === 0;
  return (
    <section
      id={section.id}
      className={`${isLight ? "bg-background" : "bg-light-blue/30"} py-12 sm:py-16`}
    >
      <div className="container-px mx-auto max-w-4xl px-4">
        {section.heading && (
          <h2 className="font-headings text-2xl sm:text-3xl font-extrabold text-primary mb-4 leading-tight">
            {section.heading}
          </h2>
        )}
        {section.intro && (
          <p className="text-base sm:text-lg text-foreground/80 leading-relaxed mb-6">
            {section.intro}
          </p>
        )}

        {variant === "text" && (
          <div className="space-y-4 text-foreground/85 text-base sm:text-lg leading-relaxed">
            {section.body}
          </div>
        )}

        {variant === "callout" && (
          <div className="rounded-2xl border-l-4 border-accent bg-card p-6 shadow-soft">
            <p className="text-base sm:text-lg text-foreground/90 leading-relaxed font-medium">
              {section.callout}
            </p>
          </div>
        )}

        {variant === "cards" && section.cards && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {section.cards.map((card, i) => (
              <div
                key={i}
                className="rounded-2xl bg-card border border-border p-6 shadow-soft hover:border-accent/40 hover:shadow-premium transition-all"
              >
                <h3 className="font-headings text-lg font-bold text-primary mb-2">
                  {card.title}
                </h3>
                <p className="text-foreground/80 text-base leading-relaxed">
                  {card.body}
                </p>
              </div>
            ))}
          </div>
        )}

        {variant === "split" && (
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="space-y-4 text-foreground/85 text-base sm:text-lg leading-relaxed">
              {section.body}
            </div>
            <div className="rounded-2xl bg-gradient-navy p-8 shadow-premium">
              <div className="space-y-3 text-primary-foreground/90 text-base leading-relaxed">
                {section.callout}
              </div>
            </div>
          </div>
        )}

        {variant === "steps" && section.steps && (
          <div className="space-y-5">
            {section.steps.map((step, i) => (
              <div
                key={i}
                className="flex gap-5 rounded-2xl bg-card border border-border p-5 sm:p-6 shadow-soft"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-accent text-white font-headings font-bold text-lg shadow-glow">
                  {i + 1}
                </div>
                <div>
                  <h3 className="font-headings text-lg font-bold text-primary mb-1">
                    {step.title}
                  </h3>
                  <p className="text-foreground/80 text-base leading-relaxed">
                    {step.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {variant === "comparison" && section.comparison && (
          <ComparisonBlock
            headers={section.comparison.headers}
            rows={section.comparison.rows}
          />
        )}

        {variant === "links" && section.links && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {section.links.map((link, i) => (
              <Link
                key={i}
                to={link.href}
                className="group flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-5 shadow-soft hover:border-accent/40 hover:shadow-premium transition-all"
              >
                <div>
                  <h3 className="font-headings text-base font-bold text-primary group-hover:text-accent transition-colors">
                    {link.title}
                  </h3>
                  {link.description && (
                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                      {link.description}
                    </p>
                  )}
                </div>
                <ArrowRight className="h-5 w-5 text-accent shrink-0 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

/* ── Video section (SAINT® explainer videos + transcript) ── */
export const VideoSectionBlock: React.FC<{
  variant?: "full" | "compact";
}> = ({ variant = "full" }) => <SaintVideoSection variant={variant} />;
